import { FC, useCallback, useEffect, useMemo, useRef, useState, KeyboardEvent } from 'react'
import { ClientLoadModel } from '@numeo/types-core'
import DOMPurify from 'dompurify'
import { Dialog } from '@mui/material'
import { useAuth } from 'hooks/auth'
import { cn } from 'utils/cn'
import { showError, showSuccess } from 'utils/snackbar'
import { useEmailChat } from '../hooks/useEmailChat'
import { EmailData } from '../types/email'
import { markLoadAsRead, reportEmailProblem } from '../api/emailApi'
import { getContactEmail, getCompany } from '../utils/apiLoadGetters'
import { formatEmailDate, isFromOurTeam, getEmailFullContent, prepareSnippetForPreview, truncateEmailHistory } from '../utils/emailChatUtils'
import { ResendEmailButton } from './ResendEmailButton'

interface EmailChatCardProps {
    row: ClientLoadModel
    isExpanded: boolean
    onUpdate?: (loadId: string, updates: Partial<ClientLoadModel>) => void
    /** Show a "Send again" icon under our own sent emails — re-sends that exact email as a reply in this thread. */
    allowResend?: boolean
    /** Outcome of each confirmed resend, so the host surface can record it in its own analytics. */
    onResent?: (outcome: 'sent' | 'failed') => void
}

/** Resend wiring shared by the inline list and the modal bubbles. */
interface ResendProps {
    contactEmail: string
    disabled: boolean
    onResend: (email: EmailData) => Promise<void>
}

export const EmailChatCard: FC<EmailChatCardProps> = ({ row, isExpanded, onUpdate, allowResend, onResent }) => {
    const { user } = useAuth()
    const messagesRef = useRef<HTMLDivElement>(null)
    const [modalOpen, setModalOpen] = useState(false)

    const threadId = row.emailThreadId || row._id
    const { emails, pendingEmails, loading, sending, refreshEmails, sendReply } = useEmailChat(row._id, threadId, isExpanded)

    const userEmails = useMemo(() => {
        return (user?.nylasEmails || []).map((e: { email: string }) => e.email.toLowerCase())
    }, [user?.nylasEmails])

    const contactEmail = getContactEmail(row) || ''
    const subject = emails[0]?.emailData?.object?.subject || `Re: Load from ${row.origin?.city || ''} to ${row.destination?.city || ''}`
    const allMessages = useMemo(() => [...emails, ...pendingEmails], [emails, pendingEmails])

    // Same content the bubble shows in full: what we sent (htmlBody while pending, else the stored body).
    // The bubble hides the icon when that is empty, so the guard only fires if the two ever drift apart —
    // and then it must say so: the dispatcher has already confirmed and would otherwise assume it went out.
    const handleResend = useCallback(
        async (email: EmailData) => {
            const htmlBody = getEmailFullContent(email.emailData, true)
            if (!htmlBody || !contactEmail) {
                showError('This email has no content to send again')
                return
            }
            const sent = await sendReply(htmlBody, contactEmail, subject)
            onResent?.(sent ? 'sent' : 'failed')
        },
        [contactEmail, subject, sendReply, onResent]
    )
    const resend: ResendProps | undefined = allowResend ? { contactEmail, disabled: sending || !contactEmail, onResend: handleResend } : undefined

    // Auto-scroll to bottom
    useEffect(() => {
        if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight
        }
    }, [allMessages])

    // Mark load as read when email chat expands & reset badge
    const rowRef = useRef(row)
    const onUpdateRef = useRef(onUpdate)
    rowRef.current = row
    onUpdateRef.current = onUpdate

    useEffect(() => {
        if (!isExpanded) return
        const currentRow = rowRef.current
        const unread = currentRow.emailHistory?.unreadCount ?? 0
        if (unread > 0) {
            markLoadAsRead(currentRow._id).catch(() => {})
            onUpdateRef.current?.(currentRow._id, { emailHistory: { ...currentRow.emailHistory, unreadCount: 0 } } as Partial<ClientLoadModel>)
        }
    }, [isExpanded])

    const companyName = getCompany(row)
    const routeLabel = `${row.origin?.city || ''}, ${row.origin?.stateProv || ''} → ${row.destination?.city || ''}, ${row.destination?.stateProv || ''}`

    return (
        <>
            {/* Inline Card — transparent, no outer bg/border (reference parity) */}
            <div className="flex-1 min-w-0 flex flex-col relative">
                {/* Header */}
                <div className="flex items-center justify-between px-1 pb-2 border-b border-[var(--lh-border)]">
                    <div className="flex items-center gap-2">
                        <svg className="w-3.5 h-3.5 text-[var(--lh-fg-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                        </svg>
                        <span className="text-[12px] font-medium text-[var(--lh-fg)]">Email Chat</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={refreshEmails}
                            disabled={loading}
                            className="w-7 h-7 rounded-md bg-transparent border-none hover:bg-[var(--lh-surface-secondary)] text-[var(--lh-fg-muted)] hover:text-[var(--lh-fg)] transition-colors disabled:opacity-50 cursor-pointer flex items-center justify-center"
                            title="Refresh emails"
                        >
                            <svg className={cn('w-3.5 h-3.5', loading && 'animate-spin')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                            </svg>
                        </button>
                        {/* Expand icon */}
                        <button
                            onClick={() => setModalOpen(true)}
                            className="w-7 h-7 rounded-md bg-transparent border-none hover:bg-[var(--lh-surface-secondary)] text-[var(--lh-fg-muted)] hover:text-[var(--lh-fg)] transition-colors cursor-pointer flex items-center justify-center"
                            title="Expand email chat"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Message List */}
                <div ref={messagesRef} className="flex-1 px-1 py-2 overflow-y-auto min-h-0 scrollbar-thin flex flex-col gap-2">
                    {loading && allMessages.length === 0 ? (
                        <p className="text-[12px] text-[var(--lh-fg-muted)] italic text-center py-6">Loading emails...</p>
                    ) : allMessages.length === 0 ? (
                        <p className="text-[12px] text-[var(--lh-fg-muted)] italic text-center py-6">No emails yet</p>
                    ) : (
                        allMessages.map((email, index) => (
                            <EmailBubble key={email.emailData?.object?.id || index} email={email} userEmails={userEmails} resend={resend} compact />
                        ))
                    )}
                </div>

                {/* Bottom Compose Area */}
                <ComposeArea sending={sending} contactEmail={contactEmail} subject={subject} onSend={sendReply} />
            </div>

            {/* Expanded Modal */}
            <EmailChatModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                loadId={row._id}
                threadId={threadId}
                companyName={companyName}
                routeLabel={routeLabel}
                allMessages={allMessages}
                userEmails={userEmails}
                loading={loading}
                sending={sending}
                contactEmail={contactEmail}
                subject={subject}
                onSend={sendReply}
                onRefresh={refreshEmails}
                resend={resend}
            />
        </>
    )
}

/** ========== Email Chat Modal ========== */
const EmailChatModal: FC<{
    open: boolean
    onClose: () => void
    loadId: string
    threadId: string
    companyName: string
    routeLabel: string
    allMessages: EmailData[]
    userEmails: string[]
    loading: boolean
    sending: boolean
    contactEmail: string
    subject: string
    onSend: (htmlBody: string, to: string, subject: string) => Promise<boolean>
    onRefresh: () => void
    resend?: ResendProps
}> = ({
    open,
    onClose,
    loadId,
    threadId,
    companyName,
    routeLabel,
    allMessages,
    userEmails,
    loading,
    sending,
    contactEmail,
    subject,
    onSend,
    onRefresh,
    resend,
}) => {
    const modalMessagesRef = useRef<HTMLDivElement>(null)
    const [showBugReport, setShowBugReport] = useState(false)
    const [bugReportMessage, setBugReportMessage] = useState('')
    const [submittingBugReport, setSubmittingBugReport] = useState(false)

    useEffect(() => {
        if (modalMessagesRef.current) {
            modalMessagesRef.current.scrollTop = modalMessagesRef.current.scrollHeight
        }
    }, [allMessages, open])

    useEffect(() => {
        if (!open) {
            setShowBugReport(false)
            setBugReportMessage('')
            setSubmittingBugReport(false)
        }
    }, [open])

    const handleBugReportSubmit = useCallback(async () => {
        const message = bugReportMessage.trim()
        if (!message || submittingBugReport) return

        setSubmittingBugReport(true)
        try {
            await reportEmailProblem({
                threadId,
                loadId,
                message,
            })
            setBugReportMessage('')
            setShowBugReport(false)
            showSuccess('Problem reported successfully')
        } catch {
            showError('Failed to submit report')
        } finally {
            setSubmittingBugReport(false)
        }
    }, [bugReportMessage, submittingBugReport, threadId, loadId])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                className: '!rounded-xl !w-[580px] !max-h-[80vh] !m-0 dark:!bg-[#1E1E1E] !shadow-xl !border !border-gray-200 dark:!border-[#333]',
            }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7EAEE] dark:border-[#333]">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[15px] font-semibold text-gray-900 dark:text-white truncate">{companyName}</span>
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="text-[13px] text-[#6B7280] dark:text-gray-400 truncate">{routeLabel}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                        onClick={() => setShowBugReport((prev) => !prev)}
                        className={cn(
                            'p-1 rounded-md transition-colors',
                            showBugReport
                                ? 'text-[#0067D5] bg-[#EEF6FF] dark:bg-[#1E2B44] dark:text-[#7EB3FF]'
                                : 'text-[#6B7280] hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-[#333]'
                        )}
                        title="Report a problem"
                        aria-label="Report a problem"
                    >
                        <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M7.5 7.5h9m-9 0v1.875m9-1.875v1.875m-9 0h9m-9 0v1.875m9-1.875v1.875m-9 0h9M9 4.5v2m6-2v2M9 17.5v2m6-2v2M6 9.375H4m2 5.25H4m16-5.25h-2m2 5.25h-2"
                            />
                        </svg>
                    </button>
                    <button
                        onClick={onRefresh}
                        disabled={loading}
                        className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-[#333] text-[#6B7280] transition-colors disabled:opacity-50"
                        title="Refresh emails"
                        aria-label="Refresh emails"
                    >
                        <svg
                            className={cn('w-[18px] h-[18px]', loading && 'animate-spin')}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                        </svg>
                    </button>
                    <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-[#333] text-[#6B7280] transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {showBugReport && (
                <div className="px-5 py-3 border-b border-[#FCD34D] bg-[#FEF3C7] dark:bg-[#4D3A12] dark:border-[#B18A2D]">
                    <div className="text-[12px] font-medium text-[#92400E] dark:text-[#FCD34D] mb-1.5">Report a problem with this email</div>
                    <textarea
                        value={bugReportMessage}
                        onChange={(e) => setBugReportMessage(e.target.value)}
                        placeholder="Describe the issue..."
                        rows={2}
                        disabled={submittingBugReport}
                        className="w-full resize-none rounded-md border border-[#FCD34D] bg-white dark:bg-[#1F2937] px-3 py-2 text-[13px] text-gray-800 dark:text-gray-200 placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#F59E0B] focus:border-[#F59E0B]"
                    />
                    <div className="flex items-center justify-end gap-2 mt-2">
                        <button
                            onClick={() => {
                                setShowBugReport(false)
                                setBugReportMessage('')
                            }}
                            disabled={submittingBugReport}
                            className="px-2 py-1 text-[12px] text-[#6B7280] hover:text-[#374151] disabled:opacity-60"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleBugReportSubmit}
                            disabled={!bugReportMessage.trim() || submittingBugReport}
                            className="min-w-[66px] px-2.5 py-1 text-[12px] font-medium text-white rounded-md bg-[#F59E0B] hover:bg-[#D97706] disabled:bg-[#FCD34D] disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {submittingBugReport ? (
                                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                'Submit'
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Messages */}
            <div ref={modalMessagesRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-[300px] max-h-[55vh]">
                {loading && allMessages.length === 0 ? (
                    <p className="text-[13px] text-[#6B7280] italic text-center py-10">Loading emails...</p>
                ) : allMessages.length === 0 ? (
                    <p className="text-[13px] text-[#6B7280] italic text-center py-10">No emails yet</p>
                ) : (
                    allMessages.map((email, index) => (
                        <EmailBubble key={email.emailData?.object?.id || index} email={email} userEmails={userEmails} resend={resend} />
                    ))
                )}
            </div>

            {/* Compose */}
            <ComposeArea sending={sending} contactEmail={contactEmail} subject={subject} onSend={onSend} isModal />
        </Dialog>
    )
}

/** ========== Compose Area ========== */
const ComposeArea: FC<{
    sending: boolean
    contactEmail: string
    subject: string
    onSend: (htmlBody: string, to: string, subject: string) => Promise<boolean>
    isModal?: boolean
}> = ({ sending, contactEmail, subject, onSend, isModal }) => {
    const [replyText, setReplyText] = useState('')
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const handleSend = useCallback(async () => {
        const trimmed = replyText.trim()
        if (!trimmed || !contactEmail || sending) return
        const htmlBody = trimmed.replace(/\n/g, '<br>')
        setReplyText('')
        if (textareaRef.current) textareaRef.current.style.height = 'auto'
        await onSend(htmlBody, contactEmail, subject)
    }, [replyText, contactEmail, sending, subject, onSend])

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
            }
        },
        [handleSend]
    )

    // Auto-grow textarea
    const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setReplyText(e.target.value)
        const el = e.target
        el.style.height = 'auto'
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`
    }, [])

    const hasContent = replyText.trim().length > 0

    const isMultiLine = replyText.includes('\n') || (textareaRef.current && textareaRef.current.scrollHeight > textareaRef.current.clientHeight)

    return (
        <div className={cn('border-t border-[var(--lh-border)]', isModal ? 'px-5 py-3' : 'px-1 py-2')}>
            <div className={cn(isMultiLine ? '' : 'flex items-center gap-2')}>
                {/* Text input */}
                <textarea
                    ref={textareaRef}
                    value={replyText}
                    onChange={handleInput}
                    onKeyDown={handleKeyDown}
                    placeholder="Write an email..."
                    rows={1}
                    className={cn(
                        'flex-1 resize-none bg-transparent text-[var(--lh-fg)] px-0 py-0 focus:outline-none placeholder:text-[var(--lh-fg-muted)] border-none',
                        isMultiLine && 'w-full',
                        isModal ? 'text-[14px]' : 'text-[12px]'
                    )}
                />

                {/* Send button — reference rounded circular (reference line 4301) */}
                <div className={cn('flex items-center justify-end flex-shrink-0', isMultiLine && 'mt-2')}>
                    <button
                        onClick={handleSend}
                        disabled={!hasContent || sending || !contactEmail}
                        className={cn(
                            'w-7 h-7 rounded-full flex items-center justify-center transition-opacity border-none cursor-pointer disabled:cursor-not-allowed',
                            hasContent && !sending
                                ? 'bg-[var(--lh-accent)] hover:bg-[var(--lh-accent-hover)] text-[var(--lh-accent-foreground)] opacity-100'
                                : 'bg-[var(--lh-accent)] text-[var(--lh-accent-foreground)] opacity-50'
                        )}
                    >
                        {sending ? (
                            <span className="animate-spin w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full" />
                        ) : (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}

/** ========== SentByAI Icon (matches extension exactly) ========== */
const SentByAIIcon: FC = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="0.5" y="0.5" width="23" height="23" rx="7.5" fill="white" />
        <rect x="0.5" y="0.5" width="23" height="23" rx="7.5" stroke="#E7EAEE" />
        <path
            d="M10.6667 8.66667L10.3227 9.596C9.872 10.8147 9.64667 11.424 9.202 11.8687C8.75733 12.3133 8.148 12.5387 6.92933 12.9893L6 13.3333L6.92933 13.6773C8.148 14.128 8.75733 14.354 9.202 14.798C9.64667 15.242 9.872 15.852 10.3227 17.0707L10.6667 18L11.0107 17.0707C11.4613 15.852 11.6873 15.2427 12.1313 14.798C12.5753 14.3533 13.1853 14.128 14.404 13.6773L15.3333 13.3333L14.404 12.9893C13.1853 12.5387 12.576 12.3133 12.1313 11.8687C11.6867 11.424 11.4613 10.8147 11.0107 9.596L10.6667 8.66667ZM16 6L15.8527 6.398C15.6593 6.92067 15.5627 7.182 15.3727 7.372C15.182 7.56267 14.9207 7.65933 14.398 7.852L14 8L14.3987 8.14733C14.9207 8.34067 15.182 8.43733 15.372 8.62733C15.5627 8.818 15.6593 9.07933 15.852 9.602L16 10L16.1473 9.602C16.3407 9.07933 16.4373 8.818 16.6273 8.628C16.818 8.43733 17.0793 8.34067 17.602 8.148L18 8L17.6013 7.85267C17.0793 7.65933 16.818 7.56267 16.628 7.37267C16.4373 7.182 16.3407 6.92067 16.148 6.398L16 6Z"
            stroke="#0029D7"
            strokeWidth="1.2"
            strokeLinejoin="round"
        />
    </svg>
)

/** ========== Attachment Images Grid ========== */
const AttachmentImages: FC<{ email: EmailData }> = ({ email }) => {
    const attachments = email.emailData?.object?.attachments?.filter((a) => a.content_type?.startsWith('image/') && !a.is_inline) || []
    const localImages = email.localAttachments?.filter((a) => a.type?.startsWith('image/')) || []

    if (attachments.length === 0 && localImages.length === 0) return null

    const maxShow = 4
    const totalImages = attachments.length + localImages.length
    const extraCount = totalImages > maxShow ? totalImages - maxShow : 0

    return (
        <div className="grid grid-cols-2 gap-1.5 mt-2">
            {localImages.slice(0, maxShow).map((img, i) => (
                <div key={`local-${i}`} className="w-[72px] h-[72px] rounded-md overflow-hidden bg-gray-100 dark:bg-[#333]">
                    {img.preview ? (
                        <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-[#8C9095]">{img.name}</div>
                    )}
                </div>
            ))}
            {attachments.slice(0, maxShow - localImages.length).map((att) => (
                <div key={att.id} className="w-[72px] h-[72px] rounded-md overflow-hidden bg-gray-100 dark:bg-[#333] flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#8C9095]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
                        />
                    </svg>
                </div>
            ))}
            {extraCount > 0 && (
                <div className="w-[72px] h-[72px] rounded-md bg-gray-100 dark:bg-[#333] flex items-center justify-center text-[12px] font-medium text-[#6B7280]">
                    +{extraCount}
                </div>
            )}
        </div>
    )
}

/** ========== File Attachments List ========== */
const AttachmentFiles: FC<{ email: EmailData }> = ({ email }) => {
    const files = email.emailData?.object?.attachments?.filter((a) => !a.content_type?.startsWith('image/') && !a.is_inline) || []
    const localFiles = email.localAttachments?.filter((a) => !a.type?.startsWith('image/')) || []

    if (files.length === 0 && localFiles.length === 0) return null

    const formatSize = (bytes: number) => (bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`)

    return (
        <div className="mt-2 space-y-1">
            {localFiles.map((f, i) => (
                <div key={`local-${i}`} className="flex items-center gap-2 px-2 py-1 rounded bg-gray-50 dark:bg-[#333]">
                    <svg className="w-4 h-4 text-[#8C9095] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                        />
                    </svg>
                    <span className="text-[11px] text-[#363739] dark:text-gray-300 truncate">{f.name}</span>
                    <span className="text-[10px] text-[#8C9095] flex-shrink-0">{formatSize(f.size)}</span>
                </div>
            ))}
            {files.map((f) => (
                <div key={f.id} className="flex items-center gap-2 px-2 py-1 rounded bg-gray-50 dark:bg-[#333]">
                    <svg className="w-4 h-4 text-[#8C9095] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                        />
                    </svg>
                    <span className="text-[11px] text-[#363739] dark:text-gray-300 truncate">{f.filename}</span>
                    <span className="text-[10px] text-[#8C9095] flex-shrink-0">{formatSize(f.size)}</span>
                </div>
            ))}
        </div>
    )
}

/** ========== Bottom Sheet (inside card) for Full Email ========== */
const EmailBottomSheet: FC<{
    email: EmailData
    fromUs: boolean
    isSentByAI: boolean
    onClose: () => void
}> = ({ email, fromUs, isSentByAI, onClose }) => {
    // Full HTML rendering: body → snippet (skip cleaned_body — it's markdown, not HTML)
    // Matches extension's EmailBody component
    const rawContent = getEmailFullContent(email.emailData, fromUs)

    const sanitized = DOMPurify.sanitize(rawContent, {
        ALLOWED_TAGS: [
            'p',
            'br',
            'strong',
            'em',
            'u',
            'a',
            'img',
            'div',
            'span',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'b',
            'i',
            'table',
            'tbody',
            'tr',
            'td',
            'th',
            'thead',
            'ul',
            'ol',
            'li',
            'hr',
            'font',
        ],
        // `color` dropped for the same reason as the bubble sanitizer below — broker mail
        // ships `<font color>` authored for a white client, which disappears on our surface.
        ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'style', 'class', 'width', 'height', 'align', 'border', 'face', 'size'],
    })

    const senderName = fromUs ? (isSentByAI ? 'Numeo AI' : 'You') : email.emailData?.object?.from?.[0]?.name || 'Broker'
    const senderEmail = email.emailData?.object?.from?.[0]?.email || ''
    const dateStr = email.emailData?.object?.date ? formatEmailDate(email.emailData.object.date) : ''
    const initial = senderName.charAt(0).toUpperCase()

    return (
        /* Overlay covers the card column only (absolute inside relative parent) */
        <div className="absolute inset-0 z-10 flex flex-col bg-black/40 rounded-lg overflow-hidden" onClick={onClose}>
            {/* Push sheet to bottom */}
            <div className="flex-1" />

            {/* Sheet */}
            <div className="bg-white dark:bg-[#1E1E1E] rounded-t-xl shadow-xl flex flex-col max-h-[85%] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-start justify-between px-3 py-2.5 border-b border-[#D8DBE0] dark:border-[#333] bg-[#F6F9FF] dark:bg-[#282828]">
                    <div className="flex items-start gap-2 min-w-0">
                        <div
                            className={cn(
                                'w-8 h-8 rounded-full flex items-center justify-center text-white text-[12px] font-semibold flex-shrink-0',
                                fromUs ? 'bg-emerald-500' : 'bg-blue-500'
                            )}
                        >
                            {initial}
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">{senderName}</span>
                                {isSentByAI && <SentByAIIcon />}
                            </div>
                            {senderEmail && <p className="text-[11px] text-[#6B7280] truncate">{senderEmail}</p>}
                            {dateStr && <p className="text-[10px] text-[#8C9095] mt-0.5">{dateStr}</p>}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-[#333] text-[#6B7280] transition-colors flex-shrink-0">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Full email body - matches extension's EmailBody styling */}
                <div className="flex-1 overflow-y-auto p-4">
                    <div
                        // Same author-color neutralization as the chat bubble above — the bottom
                        // sheet renders the identical broker HTML, so it needs the identical guard.
                        className="text-[13px] text-[#333] dark:text-gray-200 break-words [&_*:not(a)]:!text-inherit [&_*]:!bg-transparent [&_a]:!text-blue-500 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded"
                        style={{ fontFamily: 'Arial, sans-serif', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: sanitized }}
                    />
                    <AttachmentImages email={email} />
                    <AttachmentFiles email={email} />
                </div>
            </div>
        </div>
    )
}

/** ========== Email Bubble ========== */
const EmailBubble: FC<{ email: EmailData; userEmails: string[]; resend?: ResendProps; compact?: boolean }> = ({ email, userEmails, resend, compact }) => {
    const [showFullEmail, setShowFullEmail] = useState(false)
    const fromUs = isFromOurTeam(email, userEmails)
    const isSentByAI = email.isSentByAI || email.emailData?.object?.metadata?.sentBy === 'AI'
    const isPending = email.isPending && !email.isSent
    const isFailed = email.isFailed

    // For bubble: body (HTML) → snippet, with truncateEmailHistory
    // Matches extension's EmailItem: user-sent uses htmlBody, broker uses object.body
    const isUserSent = email.isPending || email.isFailed
    const snippetContent = isUserSent
        ? email.emailData?.htmlBody || email.emailData?.object?.body || ''
        : email.emailData?.object?.body || email.emailData?.object?.snippet || ''

    // Compact: strip images, keep only text formatting (like extension's prepareSnippetForPreview)
    // Full (modal bubbles): render full HTML with images
    const sanitized = compact
        ? DOMPurify.sanitize(prepareSnippetForPreview(truncateEmailHistory(snippetContent)), {
              ALLOWED_TAGS: ['strong', 'em', 'u', 'b', 'i', 's', 'strike', 'br', 'p', 'span'],
              ALLOWED_ATTR: [],
          })
        : DOMPurify.sanitize(truncateEmailHistory(snippetContent), {
              ALLOWED_TAGS: [
                  'p',
                  'br',
                  'strong',
                  'em',
                  'u',
                  'a',
                  'img',
                  'div',
                  'span',
                  'h1',
                  'h2',
                  'h3',
                  'b',
                  'i',
                  'table',
                  'tbody',
                  'tr',
                  'td',
                  'ul',
                  'ol',
                  'li',
                  'hr',
                  'font',
              ],
              // `color` is deliberately absent: brokers compose in Outlook/Gmail, which stamp
              // `<font color="#333">` (and inline `style="color:…"`) onto the body. Those are
              // authored against a WHITE mail client, so on our dark bubble they render
              // near-invisible. Dropping the attribute kills the `<font color>` half; the inline
              // `style` half is neutralized by the `[&_*:not(a)]:!text-inherit` rule on the body
              // wrapper below (we keep `style` because it also carries layout the email needs).
              // Same posture as agentic-hub's EmailIframe, which forces `color: inherit !important`
              // inside the frame.
              ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'style', 'class', 'width', 'height', 'align', 'border', 'face', 'size'],
          })

    const date = email.emailData?.object?.date
    const dateStr = date ? formatEmailDate(date) : ''

    // Sender info
    const senderLabel = fromUs ? (isSentByAI ? 'Numeo AI' : 'You') : 'Broker'
    const senderEmail = email.emailData?.object?.from?.[0]?.email || ''

    return (
        <>
            <div className={cn('flex flex-col', fromUs ? 'items-end' : 'items-start')}>
                <div
                    onClick={() => compact && setShowFullEmail(true)}
                    className={cn(
                        'relative',
                        compact ? 'px-2.5 py-2 max-w-[85%] cursor-pointer hover:opacity-90 transition-opacity' : 'px-4 py-3 max-w-[85%]',
                        fromUs
                            ? 'rounded-[12px_12px_4px_12px] bg-[var(--lh-accent)] text-[var(--lh-accent-foreground)]'
                            : 'rounded-[12px_12px_12px_4px] bg-[var(--lh-surface-secondary)] text-[var(--lh-fg)] border border-[var(--lh-border)]',
                        isPending && 'opacity-60',
                        isFailed && 'border-[var(--lh-danger)]'
                    )}
                >
                    {/* AI icon top-right */}
                    {isSentByAI && fromUs && (
                        <div className="absolute -top-3 -right-3">
                            <SentByAIIcon />
                        </div>
                    )}

                    {/* Body - compact: text-only 2-line preview, full: rich HTML with images */}
                    <div
                        className={cn(
                            'break-words',
                            fromUs ? 'text-[var(--lh-accent-foreground)]' : 'text-[var(--lh-fg)]',
                            // Broker-authored colors are written for a white mail client, so they
                            // must not survive into our themed bubble: force every descendant to
                            // inherit the bubble's own (theme-aware) color and drop author
                            // backgrounds. Links are excluded so they keep their accent color.
                            '[&_*:not(a)]:!text-inherit [&_*]:!bg-transparent',
                            fromUs ? '[&_a]:!text-[var(--lh-accent-foreground)]' : '[&_a]:!text-[var(--lh-accent)]',
                            compact
                                ? 'text-[12px] leading-[1.4] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden [&_p]:inline [&_p]:m-0 [&_br]:hidden'
                                : 'text-[13px] [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded'
                        )}
                        style={compact ? undefined : { fontFamily: 'Arial, sans-serif', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: sanitized }}
                    />

                    {/* Inline attachments */}
                    {!compact && (
                        <>
                            <AttachmentImages email={email} />
                            <AttachmentFiles email={email} />
                        </>
                    )}
                </div>

                {/* Label + email + timestamp below bubble */}
                <div className={cn('flex items-center gap-1.5 mt-1', fromUs ? 'pr-1' : 'pl-1')}>
                    <span className={cn('font-semibold text-[#8C9095]', compact ? 'text-[10px]' : 'text-[11px]')}>{senderLabel}</span>
                    {senderEmail && !isPending && <span className={cn('text-[#8C9095]', compact ? 'text-[9px]' : 'text-[10px]')}>{senderEmail}</span>}
                    {isPending && <span className="animate-spin w-2.5 h-2.5 border border-[#8C9095] border-t-transparent rounded-full" />}
                    {isFailed && (
                        <svg className="w-3 h-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    )}
                    {dateStr && (
                        <>
                            <span className={cn('text-[#6E7175]', compact ? 'text-[10px]' : 'text-[11px]')}>·</span>
                            <span className={cn('font-medium text-[#6E7175]', compact ? 'text-[10px]' : 'text-[11px]')}>{dateStr}</span>
                        </>
                    )}
                    {resend && fromUs && !isPending && getEmailFullContent(email.emailData, true) && (
                        <ResendEmailButton
                            contactEmail={resend.contactEmail}
                            disabled={resend.disabled}
                            compact={compact}
                            onConfirm={() => void resend.onResend(email)}
                        />
                    )}
                </div>
            </div>

            {/* Bottom sheet for full email (compact mode click) */}
            {showFullEmail && <EmailBottomSheet email={email} fromUs={fromUs} isSentByAI={isSentByAI} onClose={() => setShowFullEmail(false)} />}
        </>
    )
}
